/**
 * Keeps a render loop advancing while the browser tab is hidden.
 *
 * The PIXI ticker is driven by requestAnimationFrame, which a hidden tab pauses, so anything
 * awaiting a produced frame (onAllSlideImagesRendered, screenshots, offscreen renders) stalls until
 * the tab is refocused. Main-thread timers can't rescue this — they're throttled to ~1Hz when
 * hidden. A setInterval running inside a dedicated Web Worker is not throttled, so we let a worker
 * post a heartbeat and advance the ticker one frame per beat while hidden.
 *
 * Fully automatic, driven by a visibilitychange listener: the worker is started when the tab
 * becomes hidden and torn down when it becomes visible again (or on destroy). While hidden, each
 * heartbeat advances the ticker only when playback is stopped and there is still render work
 * pending; otherwise it's a cheap no-op. The worker deliberately stays alive across empty periods
 * so that render work enqueued later — while still hidden — is picked up without needing a
 * visibility transition.
 *
 * No-ops in environments without a document or Web Worker support (e.g. the video-data-server / SSR
 * path).
 */
export interface BackgroundTickerHost {
    /** Whether the owner has been destroyed. */
    readonly destroyed: boolean;
    /** Whether playback is currently running (in which case rAF already drives rendering). */
    isPlaying(): boolean;
    /** Whether the render queue has drained (nothing left to produce). */
    isRenderQueueDoneProcessing(): boolean;
    /** Advance the render loop by a single frame. */
    tick(): void;
}

const BACKGROUND_RENDER_FPS = 30;

export class BackgroundTicker {
    #host: BackgroundTickerHost;
    #worker: Worker | null = null;
    #workerUrl: string | null = null;
    #onVisibilityChange: (() => void) | null = null;

    constructor(host: BackgroundTickerHost) {
        this.#host = host;

        if (typeof document === 'undefined' || typeof Worker === 'undefined') {
            return;
        }

        this.#onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                this.#startWorker();
            } else {
                this.#stopWorker();
            }
        };
        document.addEventListener('visibilitychange', this.#onVisibilityChange);

        if (document.visibilityState === 'hidden') {
            this.#startWorker();
        }
    }

    /** Whether the background-render worker is currently running. Exposed for tests. */
    get _workerActiveForTesting(): boolean {
        return this.#worker !== null;
    }

    /** Runs the worker heartbeat handler directly. Exposed for tests. */
    _onHeartbeatForTesting() {
        this.#onHeartbeat();
    }

    destroy() {
        this.#stopWorker();
        if (this.#onVisibilityChange && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.#onVisibilityChange);
        }
        this.#onVisibilityChange = null;
    }

    #startWorker() {
        if (this.#worker || this.#host.destroyed) {
            return;
        }
        if (typeof Worker === 'undefined' || typeof Blob === 'undefined') {
            return;
        }

        const intervalMs = Math.round(1000 / BACKGROUND_RENDER_FPS);
        const source = `setInterval(() => postMessage(0), ${intervalMs});`;
        const blob = new Blob([source], { type: 'application/javascript' });
        this.#workerUrl = URL.createObjectURL(blob);
        this.#worker = new Worker(this.#workerUrl);
        this.#worker.onmessage = () => this.#onHeartbeat();
    }

    #stopWorker() {
        if (this.#worker) {
            this.#worker.terminate();
            this.#worker = null;
        }
        if (this.#workerUrl) {
            URL.revokeObjectURL(this.#workerUrl);
            this.#workerUrl = null;
        }
    }

    #onHeartbeat() {
        // Note: we intentionally do NOT gate on the ticker being started — the offscreen
        // render-queue path (scene-design thumbnails, screenshots) produces frames without ever
        // starting the playback ticker, so gating on it would skip exactly the ticks we need.
        if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
            return;
        }
        if (this.#host.destroyed) {
            return;
        }
        if (this.#host.isPlaying()) {
            return;
        }
        // Skip the tick when there's no render work, but keep the worker alive: it stays running for
        // the whole hidden period so that work enqueued later (while still hidden) is picked up. The
        // worker is only torn down on becoming visible again or on destroy. An idle heartbeat that
        // hits this early-return costs effectively nothing.
        if (this.#host.isRenderQueueDoneProcessing()) {
            return;
        }

        this.#host.tick();
    }
}
